<template>
  <div class="video-workout">
    <hero
      class="video-workout__hero"
      :image="banner"
      :video="video"
      :video-options="{ autoInitialize: true }"
      @videoReady="handlePlayerReady"
    />

    {{ /* Toolbar above hero causes video not to play */ }}
    <workout-toolbar v-if="!isPresent(session)" :workoutId="workoutId" />
    <workout-session-toolbar
      v-if="isPresent(session)"
      @complete="completeWorkout"
      @resume="resumeWorkout"
      @exitAttempted="pauseWorkout"
    />

    <template v-if="isEmpty(session)">
      <text-block-header
        raised
        :heading="workoutTitle"
        :content="workoutDescription"
      />
    </template>

    <workout-group
      v-for="group in workoutGroups"
      :key="group.id"
      :group="group"
    />

    <template v-if="isPresent(session)">
      <workout-sign-up-prompt
        v-model="showSignUpPrompt"
        @dismiss="() => completeWorkout(true)"
      ></workout-sign-up-prompt>
      <cta-row class="pt-0" white-bg no-spacing>
        <div class="w-100 py-1">
          <v-button
            block
            ghost
            secondary
            @click="() => completeWorkout()"
            :is-loading="isLoading"
          >
            Complete Workout
          </v-button>
        </div>
      </cta-row>
    </template>
    <template v-else>
      <cta-row>
        <v-button
          block
          :is-loading="isLoading || isEmpty(player)"
          @click="startWorkout"
        >
          Start Workout
        </v-button>
      </cta-row>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"

import Timer from "@/lib/timer"
import { isEmpty, isPresent } from "@/lib/utils"

import Hero from "@/components/Hero"
import CtaRow from "@/components/CtaRow"
import TextBlockHeader from "@/components/TextBlockHeader"
import WorkoutToolbar from "@/components/workout/Toolbar"
import WorkoutSessionToolbar from "@/components/workout/SessionToolbar"
import WorkoutGroup from "@/components/workout/Group"
import WorkoutSignUpPrompt from "@/components/workout/SignUpPrompt"

export default {
  components: {
    WorkoutToolbar,
    WorkoutSessionToolbar,
    WorkoutGroup,
    WorkoutSignUpPrompt,
    Hero,
    CtaRow,
    TextBlockHeader
  },

  props: {
    workoutId: { type: Number, required: true },
    workoutGroups: { type: Array, default: () => [] },
    workoutTitle: { type: String, required: true },
    workoutDescription: { type: String, default: "" },
    video: { type: Object, required: true },
    banner: { type: Object, default: () => ({}) }
  },

  data() {
    return {
      player: null,
      session: null,
      isLoading: false,
      workoutTimer: null,
      showSignUpPrompt: false
    }
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated"])
  },

  methods: {
    isEmpty,
    isPresent,

    ...mapActions("workouts", ["startSession", "completeSession"]),

    startWorkout() {
      this.player.play()
      this.isLoading = true

      this.startSession({
        workoutId: this.workoutId,
        mode: "video_workout"
      }).then(session => {
        this.session = session
        this.$emit("sessionStart", this.session)
        this.workoutTimer = new Timer({ countIn: false })
        this.workoutTimer.start()
        this.isLoading = false
      })
    },

    pauseWorkout() {
      this.player.pause()
    },

    resumeWorkout() {
      this.player.play()
    },

    completeWorkout(hasPrompted = false) {
      this.player.pause()

      if (hasPrompted || this.isAuthenticated) {
        this.isLoading = true

        this.completeSession({
          workoutId: this.workoutId,
          sessionId: this.session.id
        })
          .then(() => {
            this.player.stop()
            this.$emit("complete")
            this.session = null
          })
          .finally(() => {
            this.isLoading = false
          })
      } else {
        this.showSignUpPrompt = true
      }
    },

    handlePlayerReady(player) {
      this.player = player
      this.isLoading = false
    }
  }
}
</script>

<style lang="scss">
.video-workout__hero {
  .hero.--playing {
    height: 70vh !important;
  }
}
</style>
